import { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import formatter from "../../utils/formatter";
import { brandActionCreators } from "../../redux/seller";
import {
  Button,
  Input,
  Table,
  Tooltip,
  message,
  Modal,
  Radio,
  Alert,
  Tag,
  Spin,
  Popconfirm,
  Result,
} from "antd";
import {
  InfoCircleOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Dashboard from "../seller-mgmt/Dashboard";
import colors from "./assets/colors";
import PreviewLisitingModal from "./PreviewLisitingModal";
import ErrorBoundary from "../../components/ErrorBoundary";

const ensureArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {}
    if (val.includes(",")) {
      return val
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (val.trim()) {
      return [val.trim()];
    }
  }
  return [];
};

const renderItemText = (item) => {
  if (item === null || item === undefined) return "";
  if (typeof item === "object") {
    return (
      item.name ||
      item.letter ||
      item.size ||
      item.title ||
      item.label ||
      item.type ||
      ""
    );
  }
  return String(item);
};

const standardize_color = (str) => {
  if (!str || typeof str !== "string") return "#000000";
  let color = colors.find(
    (c) => c.name?.toLowerCase() === str.toLowerCase()
  );
  if (color?.hex) return color.hex;
  if (str.startsWith("#") || str.startsWith("rgb")) return str;
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    ctx.fillStyle = str;
    return ctx.fillStyle || "#000000";
  } catch (e) {
    return "#000000";
  }
};

const getColorHex = (color) => {
  if (!color) return "#000000";
  if (typeof color === "object") {
    if (color.hex) return color.hex;
    if (color.name) return standardize_color(color.name);
  }
  return standardize_color(String(color));
};

const getColorName = (color) => {
  if (!color) return "";
  if (typeof color === "object") {
    return color.name || color.label || "";
  }
  return String(color);
};

function capitalizeStr(string) {
  if (!string || typeof string !== "string") return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export const getMissingRequirements = (prod) => {
  const categoriesWithVarieties = ["Clothing", "Fabrics", "Shoes"];
  if (!prod || typeof prod !== "object" || Object.keys(prod).length === 0)
    return [];
  const missing = [];

  // 1. Primary image
  const hasImage =
    prod.image1 ||
    prod.img1 ||
    (Array.isArray(prod.images) && prod.images.length > 0 && prod.images[0]);
  if (!hasImage) missing.push("Primary Product Image");

  // 2. Title / Name
  if (!prod.name || !String(prod.name).trim()) missing.push("Item Title");

  // 3. Product Type
  if (!prod.product_type) {
    missing.push("Product Type (Ready to Wear / Bespoke)");
  } else if (String(prod.product_type).toLowerCase() === "bespoke") {
    if (!prod.bespoke_duration)
      missing.push("Bespoke Duration (Number of Days)");
  } else {
    const returnTypes = ensureArray(prod.product_return_type);
    if (returnTypes.length === 0) missing.push("Return Policy");
  }

  // 4. Categories
  if (!prod.main_cat && !prod.main_catid) missing.push("Main Category");
  if (!prod.sub_cat && !prod.sub_catid) missing.push("Subcategory");
  if (!prod.category_name && !prod.product_catid)
    missing.push("Classification");

  // 5. Description
  if (!prod.description || !String(prod.description).trim())
    missing.push("Item Description");

  // 6. Material info
  if (!prod.material_info || !String(prod.material_info).trim())
    missing.push("Material & Care Information");

  // 7. Price
  if (
    prod.price == null ||
    prod.price === "" ||
    isNaN(prod.price) ||
    Number(prod.price) <= 0
  ) {
    missing.push("Item Price");
  }

  // 8. Size & Fit
  if (
    prod.sub_cat === "Clothing" &&
    (!prod.size_and_fit || !String(prod.size_and_fit).trim())
  ) {
    missing.push("Size & Fit");
  }

  // 9. Weight
  if (prod.product_weight == null || prod.product_weight === "")
    missing.push("Product Weight");

  // 10. Inventory / Variety / Sizes & Colors
  const varieties = ensureArray(prod.variety || prod.varieties);
  const prodColors = ensureArray(prod.colors);
  const prodSizes = ensureArray(prod.sizes);
  if (
    categoriesWithVarieties.includes(prod.sub_cat) &&
    varieties.length === 0 &&
    (prodColors.length === 0 || prodSizes.length === 0)
  ) {
    missing.push("Inventory Details (Size, Color, Quantity or Variety)");
  }

  return missing;
};

const ListingPreview = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { slug } = useParams();

  const [isError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [rejectListingModalVisible, setRejectListingModalVisible] =
    useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSuccessModalVisible, setRejectSuccessModalVisible] =
    useState(false);
  const [approvedSuccessModalVisible, setApprovedSuccessModalVisible] =
    useState(false);

  const { info } = useSelector((state) => state.user);
  const { product } = useSelector((state) => state.brands);

  const { fetchProduct, approveProduct, deactivateProduct, rejectProduct } =
    bindActionCreators(brandActionCreators, dispatch);

  const productId = slug || window.location.pathname.split("/")[2];

  const activateProduct = async () => {
    const res = await approveProduct(
      info.token,
      product?.product_id || productId
    );
    if (res.status === "ok") {
      message.success(res.message);
      setApprovedSuccessModalVisible(true);
    }
    if (res.status === "not ok") message.error(res.message);
  };

  const onRejectProduct = async () => {
    if (rejectReason === "") return setShowError(true);
    const res = await rejectProduct(
      info.token,
      product?.product_id || productId,
      rejectReason,
    );
    if (res.status === "ok") {
      setRejectListingModalVisible(false);
      setRejectSuccessModalVisible(true);
    }
    if (res.status === "not ok") message.error(res.message);
  };

  const onDeactivateProduct = async () => {
    const res = await deactivateProduct(
      info.token,
      product?.product_id || productId
    );
    if (res.status === "ok") message.success(res.message);
    if (res.status === "not ok") message.error(res.message);
  };

  const editItem = async () => {
    const res = await fetchProduct(info.token, productId);
    if (res.status === "ok") {
      history.push("/edit-listing", { state: "listing" });
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!productId) return;
      setLoading(true);
      setFetchError(null);
      try {
        const res = await fetchProduct(info.token, productId);
        if (isMounted) {
          if (res?.status === "not ok") {
            setFetchError(res?.message || "Failed to load listing details");
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setFetchError(err?.message || "An unexpected error occurred");
          setLoading(false);
        }
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const missingRequirements = getMissingRequirements(product);
  const safeReturnTypes = ensureArray(product?.product_return_type);
  const safeOccasions = ensureArray(product?.occassion_type);
  const safeColors = ensureArray(product?.colors);
  const safeSizes = ensureArray(product?.sizes);
  const rawVariety = product?.variety || product?.varieties;
  const varietyList = ensureArray(rawVariety);

  const mainImage =
    product?.image1 ||
    product?.img1 ||
    (Array.isArray(product?.images) && product.images[0]) ||
    "";
  const image2 =
    product?.image2 ||
    product?.img2 ||
    (Array.isArray(product?.images) && product.images[1]) ||
    "";
  const image3 =
    product?.image3 ||
    product?.img3 ||
    (Array.isArray(product?.images) && product.images[2]) ||
    "";
  const image4 =
    product?.image4 ||
    product?.img4 ||
    (Array.isArray(product?.images) && product.images[3]) ||
    "";

  const openRejectModal = () => {
    if (missingRequirements.length > 0 && !rejectReason) {
      setRejectReason("incomplete-info");
    }
    setRejectListingModalVisible(true);
  };

  const missingBadge = (fieldName) => (
    <Tag color="error" style={{ borderRadius: "4px" }}>
      {fieldName ? `Missing ${fieldName}` : "Not provided (Required)"}
    </Tag>
  );

  const columns = [
    {
      title: "Item name",
      dataIndex: "itemName",
      key: "name",
      width: 300,
      render: (text, record) => (
        <p>{product?.name || record?.name || record?.itemName || "-"}</p>
      ),
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      render: (text, record) => (
        <div
          className="size-letter"
          style={{
            color: "white",
            backgroundColor: "black",
            padding: "10px",
          }}
        >
          {renderItemText(record?.size) || "-"}
        </div>
      ),
      width: 200,
    },
    {
      align: "center",
      title: "Color",
      dataIndex: "color",
      key: "color",
      render: (text, record) => {
        const colorName = getColorName(record?.color || record?.color_object);
        const colorHex = getColorHex(record?.color_object || record?.color);
        return (
          <div
            className="color-container"
            style={{ width: "110px", height: "45px" }}
          >
            <div
              style={{ background: colorHex }}
              className="color-hex-box"
            ></div>
            <div className="color-name">{colorName || "-"}</div>
          </div>
        );
      },
      width: 250,
    },
    {
      title: (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div>Quantity</div>
          <Tooltip title="Ensure online quantity matches offline availability at all times. If you sell an item offline please update item quantity ">
            <InfoCircleOutlined
              style={{
                color: "rgba(0,0,0,.45)",
                fontSize: "16px",
                marginRight: "20px",
              }}
            />
          </Tooltip>
        </div>
      ),
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record) => (
        <div className="table-quantity-container">
          <Input
            disabled
            value={record?.quantity ?? 0}
            className="input"
            type="number"
            style={{ height: "45px" }}
          />
        </div>
      ),
    },
  ];

  return (
    <Dashboard
      headerTitle="The Designer Management Portal"
      dashboardBgColor="#f7f7f7"
    >
      <div className="manage-payment-container">
        <div className="manage-listing-all-content">
          <div className="manage-payment-coverall-container">
            <div className="manage-payment-body-content-header">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  marginBottom: "8px",
                  color: "#555",
                  fontSize: "14px",
                }}
                onClick={() => history.goBack()}
              >
                <ArrowLeftOutlined />
                <span>Back to Listings</span>
              </div>
              <div className="manage-payment-body-content-header-container">
                <div
                  className="manage-payment-body-content-header-title"
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <span>Listings Preview</span>
                  {product?.status && (
                    <Tag
                      color={
                        String(product.status).toLowerCase() === "activated"
                          ? "success"
                          : String(product.status).toLowerCase() === "rejected"
                          ? "error"
                          : String(product.status).toLowerCase() ===
                            "deactivated"
                          ? "magenta"
                          : "warning"
                      }
                      style={{
                        textTransform: "capitalize",
                        fontSize: "13px",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontWeight: 500,
                      }}
                    >
                      {product.status}
                    </Tag>
                  )}
                </div>
              </div>
            </div>

            {loading && (!product || Object.keys(product).length === 0) ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "300px",
                }}
              >
                <Spin size="large" tip="Loading listing details..." />
              </div>
            ) : fetchError && (!product || Object.keys(product).length === 0) ? (
              <div style={{ padding: "40px" }}>
                <Result
                  status="warning"
                  title="Failed to Load Listing"
                  subTitle={fetchError}
                  extra={
                    <Button
                      type="primary"
                      onClick={() => window.location.reload()}
                    >
                      Retry
                    </Button>
                  }
                />
              </div>
            ) : (
              <>
                {missingRequirements.length > 0 && (
                  <div style={{ margin: "24px 24px 0" }}>
                    <Alert
                      message={
                        <div style={{ fontWeight: "bold", fontSize: "16px" }}>
                          Incomplete Listing Requirements ({missingRequirements.length} missing)
                        </div>
                      }
                      description={
                        <div style={{ marginTop: "6px" }}>
                          <p style={{ margin: "0 0 8px", color: "#555" }}>
                            This listing has incomplete mandatory requirements.
                            Please review the missing fields below before taking
                            action:
                          </p>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            {missingRequirements.map((item, idx) => (
                              <Tag
                                color="error"
                                key={idx}
                                style={{
                                  borderRadius: "4px",
                                  padding: "4px 8px",
                                  fontSize: "13px",
                                  fontWeight: 500,
                                }}
                              >
                                ✕ {item}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      }
                      type="error"
                      showIcon
                      style={{
                        border: "1px solid #ffa39e",
                        borderRadius: "8px",
                        backgroundColor: "#fff1f0",
                      }}
                    />
                  </div>
                )}

                <div className="manage-payment-body-container" style={{ marginTop: "24px" }}>
                  <div className="manage-payment-body-content">
                    <div
                      className="manage-payment-body-content-section"
                      style={{ position: "relative" }}
                    >
                      <div className="product-main-container">
                        <div className="images-section">
                          {mainImage ? (
                            <div
                              className="image-large"
                              style={{
                                backgroundImage: `url(${mainImage})`,
                                backgroundSize: "cover",
                                backgroundPosition: "top",
                              }}
                            ></div>
                          ) : (
                            <div
                              className="image-large image-missing-placeholder"
                              style={{
                                width: "250px",
                                height: "250px",
                                backgroundColor: "#f5f5f5",
                                border: "2px dashed #ff4d4f",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                padding: "16px",
                                textAlign: "center",
                              }}
                            >
                              <InfoCircleOutlined
                                style={{ fontSize: "28px", color: "#ff4d4f" }}
                              />
                              <Tag color="error">No Primary Image Uploaded</Tag>
                            </div>
                          )}
                          <div className="images-thumbnail-container">
                            <div
                              style={{
                                backgroundImage: image2
                                  ? `url(${image2})`
                                  : "none",
                                backgroundColor: image2
                                  ? "transparent"
                                  : "#fafafa",
                                backgroundSize: "cover",
                              }}
                            ></div>
                            <div
                              style={{
                                backgroundImage: image3
                                  ? `url(${image3})`
                                  : "none",
                                backgroundColor: image3
                                  ? "transparent"
                                  : "#fafafa",
                                backgroundSize: "cover",
                              }}
                            ></div>
                            <div
                              style={{
                                backgroundImage: image4
                                  ? `url(${image4})`
                                  : "none",
                                backgroundColor: image4
                                  ? "transparent"
                                  : "#fafafa",
                                backgroundSize: "cover",
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="manage-payment-body-container">
                  <div className="manage-payment-body-content">
                    <div
                      className="manage-payment-body-content-section"
                      style={{ position: "relative" }}
                    >
                      <div className="preview-main-content-header">
                        <div
                          style={{
                            fontSize: "18px",
                            fontFamily: "DomaineSansText-Regular",
                            textTransform: "capitalize",
                          }}
                        >
                          {product?.brand_name || (
                            <span style={{ color: "#999" }}>Brand Unknown</span>
                          )}
                        </div>
                        <div style={{ cursor: "pointer" }} onClick={editItem}>
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 14 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10.332 1.50004C10.5071 1.32494 10.715 1.18605 10.9438 1.09129C11.1725 0.996527 11.4177 0.947754 11.6654 0.947754C11.913 0.947754 12.1582 0.996527 12.387 1.09129C12.6157 1.18605 12.8236 1.32494 12.9987 1.50004C13.1738 1.67513 13.3127 1.883 13.4074 2.11178C13.5022 2.34055 13.551 2.58575 13.551 2.83337C13.551 3.08099 13.5022 3.32619 13.4074 3.55497C13.3127 3.78374 13.1738 3.99161 12.9987 4.16671L3.9987 13.1667L0.332031 14.1667L1.33203 10.5L10.332 1.50004Z"
                              stroke="black"
                              strokeWidth="0.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <span style={{ marginLeft: "4px" }}>Edit</span>
                        </div>
                      </div>

                      <div className="preview-main-content">
                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Item title
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.name || missingBadge("title")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Ready to wear or Bespoke
                          </div>
                          <div className="preview-main-content-row-data txt-type">
                            {product?.product_type || missingBadge("type")}
                          </div>
                        </div>

                        {String(product?.product_type).toLowerCase() === "bespoke" && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Number of Days
                            </div>
                            <div className="preview-main-content-row-data">
                              {product?.bespoke_duration ? (
                                `${product.bespoke_duration} Days`
                              ) : (
                                missingBadge("duration")
                              )}
                            </div>
                          </div>
                        )}

                        {String(product?.product_type).toLowerCase() !== "bespoke" && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Return policy
                            </div>
                            <div className="preview-main-content-row-data">
                              {safeReturnTypes.length > 0 ? (
                                safeReturnTypes.map((type, idx) => (
                                  <span key={idx}>
                                    {renderItemText(type)}{" "}
                                  </span>
                                ))
                              ) : (
                                missingBadge("return policy")
                              )}
                            </div>
                          </div>
                        )}

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Main Category
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.main_cat || missingBadge("category")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Subcategory
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.sub_cat || missingBadge("subcategory")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Classification
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.category_name ||
                              missingBadge("classification")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Occasions
                          </div>
                          <div className="preview-main-content-row-data">
                            {safeOccasions.length > 0 ? (
                              <ul style={{ paddingInlineStart: "16px" }}>
                                {safeOccasions.map((type, idx) => (
                                  <li key={idx}>{renderItemText(type)}</li>
                                ))}
                              </ul>
                            ) : (
                              <span style={{ color: "#999" }}>
                                None specified
                              </span>
                            )}
                          </div>
                        </div>

                        {product?.sub_cat === "Clothing" && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Exclusive Group
                            </div>
                            <div className="preview-main-content-row-data">
                              {product?.group || (
                                <span style={{ color: "#999" }}>
                                  Not specified
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {product?.sub_cat === "Clothing" && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Fit
                            </div>
                            <div className="preview-main-content-row-data">
                              {product?.size_and_fit || missingBadge("fit")}
                            </div>
                          </div>
                        )}

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Item Description
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.description ||
                              missingBadge("description")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Material and Care Information
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.material_info ||
                              missingBadge("material & care")}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Price
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.price != null &&
                            product?.price !== "" &&
                            !isNaN(product?.price) ? (
                              formatter(product.price)
                            ) : (
                              missingBadge("price")
                            )}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Color
                          </div>
                          <div className="preview-main-content-row-data">
                            {safeColors.length > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "24px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {safeColors.map((color, idx) => (
                                  <div
                                    key={idx}
                                    className="product-color-section"
                                  >
                                    <div
                                      className="product-color-section-box"
                                      style={{
                                        background: getColorHex(color),
                                      }}
                                    ></div>
                                    <div>{getColorName(color)}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              missingBadge("color")
                            )}
                          </div>
                        </div>
                        
                        {["Clothing", "Fabrics", "Shoes"].includes(
                          product?.sub_cat,
                        ) && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Size
                            </div>
                            <div className="preview-main-content-row-data">
                              {safeSizes.length > 0 ? (
                                <div style={{ display: "flex", gap: "24px" }}>
                                  <ul style={{ paddingInlineStart: "16px" }}>
                                    {safeSizes.map((size, idx) => (
                                      <li key={idx}>{renderItemText(size)}</li>
                                    ))}
                                  </ul>
                                </div>
                              ) : (
                                missingBadge("size")
                              )}
                            </div>
                          </div>
                        )}

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Returns policy
                          </div>
                          <div className="preview-main-content-row-data">
                            {safeReturnTypes.length > 0 ? (
                              <ul style={{ paddingInlineStart: "16px" }}>
                                {safeReturnTypes.map((type, idx) => (
                                  <li key={idx}>{renderItemText(type)}</li>
                                ))}
                              </ul>
                            ) : (
                              missingBadge("returns policy")
                            )}
                          </div>
                        </div>

                        {varietyList.length > 0 && (
                          <div className="preview-main-content-row">
                            <div className="preview-main-content-row-key">
                              Inventory / Varieties
                            </div>
                            <div className="preview-main-content-row-data">
                              <Table
                                dataSource={varietyList}
                                columns={columns}
                                pagination={false}
                                rowKey={(record, idx) =>
                                  record?.id || record?._id || idx
                                }
                              />
                            </div>
                          </div>
                        )}

                        {product?.has_discount && (
                          <>
                            <div className="preview-main-content-row">
                              <div className="preview-main-content-row-key">
                                Discount
                              </div>
                              <div className="preview-main-content-row-data">
                                {product?.discounts_percent ||
                                  product?.discount_percent ||
                                  0}
                                %
                              </div>
                            </div>
                            <div className="preview-main-content-row">
                              <div className="preview-main-content-row-key">
                                Discount Date
                              </div>
                              <div className="preview-main-content-row-data">
                                {product?.discounts_start_date ||
                                  product?.discount_start_date}{" "}
                                -{" "}
                                {product?.discounts_end_date ||
                                  product?.discount_end_date}
                              </div>
                            </div>
                          </>
                        )}

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Delivery
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.delivery_type &&
                            product.delivery_type !== "losode"
                              ? capitalizeStr(product.delivery_type)
                              : "We work with third-party delivery providers to handle your delivery"}
                          </div>
                        </div>

                        <div className="preview-main-content-row">
                          <div className="preview-main-content-row-key">
                            Weight
                          </div>
                          <div className="preview-main-content-row-data">
                            {product?.product_weight != null &&
                            product?.product_weight !== "" ? (
                              `${product.product_weight} kg`
                            ) : (
                              missingBadge("weight")
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="listing-preview-actions-bar">
                  <div className="listing-preview-actions-left">
                    <Button
                      className="preview-btn-secondary"
                      onClick={() => setPreviewModalVisible(true)}
                      icon={<EyeOutlined />}
                    >
                      Preview
                    </Button>

                    <Button
                      className="preview-btn-secondary"
                      onClick={editItem}
                      icon={<EditOutlined />}
                    >
                      Edit Listing
                    </Button>
                  </div>

                  <div className="listing-preview-actions-right">
                    {String(product?.status).toLowerCase() === "activated" ? (
                      <Button
                        className="preview-btn-danger"
                        onClick={onDeactivateProduct}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="preview-btn-danger"
                          onClick={openRejectModal}
                        >
                          Reject
                        </Button>

                        {missingRequirements.length > 0 ? (
                          <Popconfirm
                            placement="top"
                            title={
                              <div style={{ maxWidth: "280px" }}>
                                <div
                                  style={{
                                    fontWeight: "bold",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Listing Has Incomplete Requirements:
                                </div>
                                <ul
                                  style={{
                                    paddingLeft: "16px",
                                    margin: "4px 0",
                                  }}
                                >
                                  {missingRequirements
                                    .slice(0, 3)
                                    .map((req, i) => (
                                      <li key={i}>{req}</li>
                                    ))}
                                  {missingRequirements.length > 3 && (
                                    <li>
                                      +{missingRequirements.length - 3} more
                                    </li>
                                  )}
                                </ul>
                                Are you sure you want to approve this listing?
                              </div>
                            }
                            onConfirm={activateProduct}
                            okText="Yes, Approve"
                            cancelText="Cancel"
                          >
                            <Button className="preview-btn-primary">
                              Approve
                            </Button>
                          </Popconfirm>
                        ) : (
                          <Button
                            className="preview-btn-primary"
                            onClick={activateProduct}
                          >
                            Approve
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <PreviewLisitingModal
          product={product}
          modalVisible={previewModalVisible}
          setModalVisible={setPreviewModalVisible}
        />

        <Modal
          centered
          visible={rejectListingModalVisible}
          onCancel={() => setRejectListingModalVisible(false)}
          footer={null}
          className="rejectListingModal"
          width={"60%"}
          // closeIcon={
          // <CloseCircleFilled
          // style={{ color: "#000", padding: "40px" }}
          maskClosable={false}
        >
          <div style={{ padding: "0 20px", fontSize: "14px" }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "24px",
                marginBottom: "4px",
              }}
            >
              Why are you rejecting this listing?
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Select your reason below
            </div>
            <div>
              <Radio.Group
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value);
                  if (isError) setShowError(false);
                }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "16px",
                  margin: "30px auto 16px",
                }}
              >
                <Radio value="unclear-image">Unclear image(s)</Radio>
                <Radio value="incomplete-info">Incomplete information</Radio>
                <Radio value="invalid-item">
                  Item cannot be sold on Losode
                </Radio>
                <Radio value="price-incorrect">
                  Item price appears to be incorrect
                </Radio>
                <Radio value="reason not here">My reason is not here</Radio>
              </Radio.Group>
              {rejectReason === "reason not here" && (
                <Input.TextArea
                  style={{ minHeight: "100px", marginTop: "16px" }}
                  className="no-reason-textbox"
                  placeholder="Enter reason here"
                />
              )}
              {isError && (
                <p style={{ color: "red" }}>Please Select a reason</p>
              )}
            </div>

            <div style={{ display: "flex", gap: "40px", marginTop: "40px" }}>
              <Button
                className="reject-btn-save"
                onClick={() => {
                  onRejectProduct();
                }}
              >
                Reject
              </Button>

              <Button
                className="reject-btn-cancel"
                onClick={() => {
                  setRejectListingModalVisible(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          centered
          visible={rejectSuccessModalVisible}
          onCancel={() => setRejectSuccessModalVisible(false)}
          footer={null}
          className="rejectListingSuccessModal"
          width={"60%"}
          // closeIcon={
          // <CloseCircleFilled
          // style={{ color: "#000", padding: "40px" }}
          maskClosable={false}
        >
          <div style={{ textAlign: "center" }}>
            An email has been sent to the designer notifying them that their
            listing has been rejected
          </div>
        </Modal>

        <Modal
          centered
          visible={approvedSuccessModalVisible}
          onCancel={() => setApprovedSuccessModalVisible(false)}
          footer={null}
          className="rejectListingSuccessModal"
          width={"60%"}
          // closeIcon={
          // <CloseCircleFilled
          // style={{ color: "#000", padding: "40px" }}
          maskClosable={false}
        >
          <div style={{ textAlign: "center" }}>
            You have now approved this listing. <br />
            An email has been sent to the designer notifying them that their
            listing has been approved
          </div>
        </Modal>

        <style jsx="true">{`
          .manage-payment-container {
            font-family: DomaineSansText-Light;
            color: black;
            margin-top: -32px;
          }
          .manage-payment-coverall-container {
          }
          .manage-listing-all-content {
            position: relative;
          }

          .manage-payment-adspace {
            height: 211px;
            background-repeat: no-repeat;
            width: 80%;
            background-position: bottom;
            background-color: black;
            margin: auto;
          }

          .manage-payment-body-container {
            margin-bottom: 40px;
          }
          .manage-payment-body-content-header-container {
          }
          .manage-payment-body-content-header {
            background: white;
            padding: 24px;
          }
          .manage-payment-body-content-header-title {
            font-size: 24px;
            font-family: "DomaineSansText-Regular";
          }
          .manage-payment-body-content-header-subtitle {
            font-size: 16px;
            color: #707070;
          }
          .manage-payment-body-sidebar-add-listing-button {
            height: 47px;
            background: white;
            color: black;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .confirm-listing-button {
            height: 48px;
            background: black;
            color: white;
            font-size: 16px;
            width: 180px;
            margin-left: 20px;
          }

          .manage-payment-body-content-listings-container {
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 70px;
          }

          .manage-payment-body-content-top-actions {
            display: flex;
            gap: 40px;
            margin-bottom: 24px;
            align-items: center;
          }
          .top-actions-checkbox-container {
            padding: 5px;
            border: 1px solid #d4d4d4;
            border-radius: 4px;
          }
          .pagination-nav-text {
            font-family: "DomaineSansText-Light";
          }
          .pagination-nav-text:hover {
            color: #800000;
          }
          .activate-delete-button {
            background: black;
            color: white;
          }

          .manage-payment-body-content {
            width: 100%;
          }
          .manage-payment-body-content-listings-container {
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 70px;
            // gap: 21px;
          }

          .manage-payment-body-content-top-actions {
            display: flex;
            gap: 40px;
            margin-bottom: 24px;
          }
          .top-actions-checkbox-container {
            padding: 5px;
            border: 1px solid #d4d4d4;
            border-radius: 4px;
          }
          .pagination-nav-text {
            font-family: "DomaineSansText-Light";
          }
          .pagination-nav-text:hover {
            color: #800000;
          }
          .activate-delete-button {
            background: black;
            color: white;
          }

          .no-listings-to-show {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 500px;
            font-weight: bold;
            font-size: 18px;
          }
          .no-listings-to-show-link {
            text-decoration: underline;
          }
          .no-listings-to-show-link:hover {
            cursor: pointer;
            color: #800000;
          }
          .ant-select-item-option-selected:not(
            .ant-select-item-option-disabled
          ) {
            background: white;
          }

          .manage-payment-payment-summary-section {
            display: flex;
            justify-content: space-between;
            width: 88%;
          }
          .payment-summary-grey-box {
            background: #f9f9f9;
            padding: 24px;
          }
          .payment-summary-pink-box {
            background: rgba(128, 0, 0, 0.05);
            padding: 24px;
            width: 450px;
          }
          .payment-summary-big-number {
            font-size: 24px;
            font-weight: bold;
            font-family: "DomaineSansText-Black";
            margin-top: 8px;
          }
          .payment-summary-pink-box-button {
            background: black;
            color: white;
            height: 47px;
            font-size: 16px;
            margin-top: 4px;
          }
          .payment-summary-underline-link {
            margin-top: 10px;
            padding: 16px 0;
            border-top: 1px solid #d4d4d4;
            font-size: 12px;
            color: #6f6f6f;
            text-decoration: underline;
          }
          .manage-payment-body-content-section-title {
            font-size: 18px;
            margin-bottom: 16px;
            font-weight: bold;
          }

          .manage-payment-body-content-trade-section-title {
            font-size: 18px;
            font-weight: bold;
          }

          . .manage-payment-body-content-section-subtitle {
            font-size: 16px;
          }

          .manage-payment-body-content-section {
            margin-top: 24px;
            background: white;
            padding: 40px 24px;
          }
          .recent-transaction-image {
            background-repeat: no-repeat;
            width: 40px;
            height: 40px;
            background-size: cover;
            border-radius: 50%;
          }
          .ant-input-search
            > .ant-input-group
            > .ant-input-group-addon:last-child
            .ant-input-search-button {
            background: #000;
            border: 1px solid #000;
            color: #fff;
            height: 48px;
            font-family: "DomaineSansText-Light";
            padding: 0 24px;
          }
          .ant-input-search
            .ant-input-group
            .ant-input-affix-wrapper:not(:last-child) {
            height: 48px;
          }

          .images-section {
            display: flex;
            gap: 16px;
          }
          .images-thumbnail-container {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          .images-thumbnail-container div {
            width: 140px;
            height: 140px;
            background-size: cover;
            background-repeat: no-repeat;
          }
          .image-large {
            width: 250px;
            height: 250px;
            background-image: url(${product?.image1});
            background-size: cover;
            background-repeat: no-repeat;
            position: relative;
          }
          .txt-product-status {
            text-transform: capitalize;
          }
          .product-image-tag-label {
            position: absolute;
            bottom: 0;
            text-align: center;
            width: 100%;
            background: #0000004d;
            font-size: 28px;
            color: white;
          }
          .product-main-container {
            display: flex;
            gap: 16px;
          }
          .product-content-section {
            display: flex;
            flex-direction: column;
            gap: 6px;
          }
          .product-color-section {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          .product-color-section-box {
            width: 14px;
            height: 14px;
            border-radius: 50%;
          }
          .product-designer {
            font-family: "DomaineSansText-Regular";
            font-size: 18px;
            font-weight: bold;
          }
          .product-extra-container {
            display: flex;
            margin-top: 40px;
            border-top: 1px solid #d4d4d4;
            padding-top: 40px;
            gap: 4px;
          }
          .product-extra {
            width: 24%;
          }
          .product-extra-divider {
            border-left: 1px solid #d4d4d4;
            margin: 0 8px;
          }
          .product-extra-title {
            font-size: 16px;
            font-family: "DomaineSansText-Regular";
            margin-bottom: 8px;
          }
          .product-extra-details {
            font-size: 12px;
            margin-bottom: 8px;
          }
          .store-preview-status {
            position: absolute;
            right: 24px;
            top: 40px;
            font-size: 16px;
            font-family: "DomaineSansText-Light";
            padding: 16px 32px;
            border: 0;
          }
          .preview-main-content-header {
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid rgb(212, 212, 212);
            padding-bottom: 16px;
            align-items: center;
          }
          .preview-main-content {
            margin: 16px 0 0;
          }
          .preview-main-content-row {
            padding: 16px 0;
            display: grid;
            grid-template-columns: 30% 1fr;
            grid-gap: 24px;
          }
          .preview-main-content-row-data {
            font-family: "DomaineSansText-Regular";
            width: 90%;
          }

          .txt-type {
            text-transform: capitalize;
          }
          .listing-preview-actions-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            margin-top: 32px;
            margin-bottom: 40px;
            padding-top: 24px;
            border-top: 1px solid #e8e8e8;
          }
          .listing-preview-actions-left,
          .listing-preview-actions-right {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 12px;
          }
          .preview-btn-primary,
          .preview-button-black {
            height: 46px;
            min-width: 130px;
            background: #000000;
            border: 1px solid #000000;
            color: #ffffff;
            font-size: 15px;
            font-weight: 500;
            border-radius: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 24px;
            transition: all 0.2s;
          }
          .preview-btn-primary:hover,
          .preview-button-black:hover {
            background: #262626;
            border-color: #262626;
            color: #ffffff;
          }
          .preview-btn-secondary,
          .preview-button-white {
            height: 46px;
            min-width: 120px;
            color: #000000;
            background: #ffffff;
            border: 1px solid #000000;
            font-size: 15px;
            font-weight: 500;
            border-radius: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 20px;
            transition: all 0.2s;
          }
          .preview-btn-secondary:hover,
          .preview-button-white:hover {
            background: #f5f5f5;
            border-color: #000000;
            color: #000000;
          }
          .preview-btn-danger,
          .preview-button-red {
            height: 46px;
            min-width: 120px;
            color: #eb5757;
            background: #ffffff;
            border: 1px solid #eb5757;
            font-size: 15px;
            font-weight: 500;
            border-radius: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0 20px;
            transition: all 0.2s;
          }
          .preview-btn-danger:hover,
          .preview-button-red:hover {
            background: #fff1f0;
            border-color: #d9363e;
            color: #d9363e;
          }
          .color-container {
            padding: 8px;
            border: 1px solid black;
            width: 120px;
            display: flex;
            align-items: center;
            gap: 10px;
            justify-content: center;
            white-space: nowrap;
            margin: 0 auto;
          }
          .color-hex-box {
            width: 12px;
            height: 12px;
            border-radius: 50%;
          }
          .table-quantity-container .ant-input[disabled] {
            color: #000;
            background-color: #fff;
          }

          .rejectListingModal {
            font-family: "DomaineSansText-Light";
          }
          .rejectListingModal.ant-modal-body {
            padding: 40px 40px 60px;
            color: black;
          }
          .reject-btn-save {
            background: #000000;
            border-color: #000;
            color: #fff;
            height: 48px;
            width: 100%;
          }
          .reject-btn-cancel {
            color: #000000;
            border-color: #000;
            background: #fff;
            height: 48px;
            width: 100%;
          }
          .input {
            height: 48px;
          }
          .ant-form-item-explain-error {
            margin-top: 4px;
          }

          .rejectListingSuccessModal .ant-modal-body {
            padding: 60px 40px;
            color: black;
            font-size: 18px;
            font-family: "DomaineSansText-Light";
          }
        `}</style>
      </div>
    </Dashboard>
  );
};

const ListingPreviewWithBoundary = (props) => (
  <ErrorBoundary>
    <ListingPreview {...props} />
  </ErrorBoundary>
);

export default ListingPreviewWithBoundary;
